/** Brief confirmation that an administrative action landed.
 *
 * SCOPE, deliberately narrow: a toast is for an outcome the reader needs to KNOW but does not
 * need to DECIDE anything about - "the door was removed from the registry", "that key was
 * revoked". A door control outcome never comes through here. `OutcomeDialog` exists precisely
 * because a message saying a door may be physically held unlocked must not fade away after four
 * seconds while the operator is looking at the door instead of the screen.
 *
 * `useToast()` returns a no-op when there is no provider above it, rather than throwing. A unit
 * test that renders one page in isolation, or a future screen mounted outside the shell, should
 * not crash because a confirmation had nowhere to go - the action itself still succeeded.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";

export type ToastTone = "ok" | "critical";

interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastApi {
  show: (message: string, tone?: ToastTone) => void;
}

const NO_PROVIDER: ToastApi = { show: () => {} };

const ToastContext = createContext<ToastApi | null>(null);

/** How long each tone stays. A failure gets longer, because the reader has to do something
 * about it and may be reading the row it refers to rather than the corner of the screen. */
const DISMISS_MS: Record<ToastTone, number> = { ok: 4500, critical: 9000 };

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  /** Every pending timer, so unmounting cannot leave one to fire into a dead tree. */
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    (message: string, tone: ToastTone = "ok") => {
      const id = nextId.current++;
      setToasts((current) => [...current, { id, tone, message }]);

      const timer = setTimeout(() => {
        timers.current.delete(timer);
        dismiss(id);
      }, DISMISS_MS[tone]);
      timers.current.add(timer);
    },
    [dismiss],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}

      {/* Outside the routed content so a toast survives a navigation, and marked as a live
          region so a screen reader is told what happened rather than only sighted users. */}
      <div className="toasts" aria-live="polite" aria-atomic="false">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={toast.tone === "critical" ? "toast toast-critical" : "toast toast-ok"}
            role={toast.tone === "critical" ? "alert" : "status"}
          >
            <span className="toast-icon" aria-hidden="true">
              {toast.tone === "critical" ? "✗" : "✓"}
            </span>
            <div className="toast-body">{toast.message}</div>
            <button
              type="button"
              className="toast-dismiss"
              aria-label="Dismiss this message"
              title="Dismiss"
              onClick={() => dismiss(toast.id)}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  return useContext(ToastContext) ?? NO_PROVIDER;
}
